import { useState, useEffect } from 'react'
import Card, { CardHeader } from '../ui/Card'
import StatNumber from '../ui/StatNumber'
import SectionLabel from '../ui/SectionLabel'
import LoadingBear from '../ui/LoadingBear'
import { fetchHealth } from '../../api/client'

export default function HealthPanel() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchHealth().then(setData).finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingBear message="Checking vitals..." />

  return (
    <div>
      <SectionLabel>Panel 1 — System Health</SectionLabel>

      {/* Health Score Ring */}
      <Card delay={0.05}>
        <CardHeader title="System Health" badge={data?.status || 'Unknown'} badgeVariant="sage" />
        <div className="flex items-center justify-center py-8">
          <HealthRing score={data?.score ?? 0} />
        </div>
      </Card>

      {/* Metric Cards */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-4 mt-4">
        {[
          { label: 'Entities', value: data?.entities ?? '—', color: 'gold' },
          { label: 'Observations', value: data?.observations ?? '—', color: 'gold' },
          { label: 'Journals', value: data?.journals ?? '—', color: 'gold' },
          { label: 'Relations', value: data?.relations ?? '—', color: 'sage' },
          { label: 'Unprocessed', value: data?.unprocessed ?? '—', color: data?.unprocessed > 100 ? 'persimmon' : 'gold' },
          { label: 'Orphans', value: data?.orphans ?? '—', color: 'parchment' },
        ].map((stat, i) => (
          <Card key={stat.label} delay={0.1 + i * 0.05}>
            <StatNumber value={stat.value} label={stat.label} color={stat.color} size="md" />
          </Card>
        ))}
      </div>
    </div>
  )
}

function HealthRing({ score }) {
  const radius = 70
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference
  const color = score >= 80 ? 'var(--mm-viridian)' : score >= 50 ? 'var(--mm-gold)' : 'var(--mm-persimmon)'
  const glowColor = score >= 80 ? 'var(--mm-viridian-glow)' : score >= 50 ? 'var(--mm-gold-glow)' : 'var(--mm-persimmon-glow)'

  return (
    <div className="relative">
      <svg width="180" height="180" className="transform -rotate-90">
        {/* Background ring */}
        <circle
          cx="90" cy="90" r={radius}
          fill="none"
          stroke="var(--mm-card-border)"
          strokeWidth="8"
        />
        {/* Score ring */}
        <circle
          cx="90" cy="90" r={radius}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            filter: `drop-shadow(0 0 10px ${glowColor})`,
            transition: 'stroke-dashoffset 1s ease-out',
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-glacial-bold text-4xl" style={{ color }}>{score}</span>
        <span className="text-[11px] text-mm-parchment/50 uppercase tracking-wider">Health</span>
      </div>
    </div>
  )
}
